import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { FileSignature, Download, ClipboardCheck, Upload, Trash2 } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import ToothChart from "../components/ToothChart";

// Static fallback catalogue (also seeded in DB)
const CONSENTS = [
  { id: "pulpotomy",      static_file: "pulpotomy.pdf",      titleAr: "موافقة على علاج العصب (الجزئي) في الأسنان اللبنية", titleEn: "Pulpotomy / Pulpal Therapy on Primary Teeth" },
  { id: "ss_crown",       static_file: "ss_crown.pdf",       titleAr: "موافقة تركيب التلبيسة المعدنية للطفل",              titleEn: "Stainless-Steel Crown (Child)" },
  { id: "nitrous_oxide",  static_file: "nitrous_oxide.pdf",  titleAr: "موافقة مستنيرة على استخدام غاز أكسيد النيتروز",      titleEn: "Nitrous Oxide (Laughing Gas)" },
  { id: "extraction",     static_file: "extraction.pdf",     titleAr: "إقرار وموافقة على خلع الأسنان",                       titleEn: "Tooth Extraction" },
  { id: "crowns_bridges", static_file: "crowns_bridges.pdf", titleAr: "إقرار وموافقة مستنيرة لعلاج التيجان والجسور السنية", titleEn: "Crowns and Bridges Treatment" },
];

const formatDateDDMMYYYY = (d = new Date()) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

// ===== TOP overlay: Date / Patient Name / National ID / File No. =====
const buildTopOverlayPng = ({ patientName, fileNumber, nationalId }) => {
  // Compact horizontal strip (height reduced for tighter look)
  const W = 1100;
  const H = 96;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);

  const PURPLE = "#655dc6";
  const GREY = "#333";
  const LIGHT = "#FAF9FE";

  // Outer rounded rectangle (filled light)
  const r = 10;
  ctx.fillStyle = LIGHT;
  ctx.strokeStyle = PURPLE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(W - r, 0);
  ctx.quadraticCurveTo(W, 0, W, r);
  ctx.lineTo(W, H - r);
  ctx.quadraticCurveTo(W, H, W - r, H);
  ctx.lineTo(r, H);
  ctx.quadraticCurveTo(0, H, 0, H - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  const cols = [
    { en: "Date",         ar: "التاريخ",      value: formatDateDDMMYYYY() },
    { en: "Patient Name", ar: "اسم المريض",   value: patientName || "—" },
    { en: "National ID",  ar: "رقم الهوية",    value: nationalId || "—" },
    { en: "File No.",     ar: "رقم الملف",    value: fileNumber || "—" },
  ];

  const colW = W / cols.length;
  ctx.textBaseline = "middle";

  cols.forEach((c, i) => {
    const cx = i * colW;
    if (i > 0) {
      ctx.strokeStyle = "#d8d3ee";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cx, 8);
      ctx.lineTo(cx, H - 8);
      ctx.stroke();
    }

    // Labels: tighter top spacing
    ctx.fillStyle = PURPLE;
    ctx.font = "600 14px Arial, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(c.en, cx + 10, 18);
    ctx.textAlign = "right";
    ctx.fillText(c.ar, cx + colW - 10, 18);

    // Values
    ctx.fillStyle = GREY;
    ctx.font = "bold 20px Arial, sans-serif";
    ctx.textAlign = "center";
    const maxW = colW - 20;
    let v = c.value;
    if (ctx.measureText(v).width > maxW) {
      while (v.length > 1 && ctx.measureText(v + "…").width > maxW) v = v.slice(0, -1);
      v = v + "…";
    }
    ctx.fillText(v, cx + colW / 2, H - 26);
  });

  const dataUrl = canvas.toDataURL("image/png");
  const base64 = dataUrl.split(",")[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

// ===== Tooth numbers bar (only if teeth selected) =====
const buildTeethBarPng = (teeth) => {
  if (!teeth || teeth.length === 0) return null;
  const W = 1100;
  const H = 38;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);

  const PURPLE = "#655dc6";
  const GREY = "#333";
  const LIGHT = "#FAF9FE";

  const r = 7;
  ctx.fillStyle = LIGHT;
  ctx.strokeStyle = PURPLE;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(W - r, 0);
  ctx.quadraticCurveTo(W, 0, W, r);
  ctx.lineTo(W, H - r);
  ctx.quadraticCurveTo(W, H, W - r, H);
  ctx.lineTo(r, H);
  ctx.quadraticCurveTo(0, H, 0, H - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.textBaseline = "middle";
  ctx.fillStyle = PURPLE;
  ctx.font = "600 14px Arial, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("Tooth No.:", 12, H / 2);
  ctx.textAlign = "right";
  ctx.fillText("رقم السن", W - 12, H / 2);

  ctx.fillStyle = GREY;
  ctx.font = "bold 17px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(teeth.join("  •  "), W / 2, H / 2);

  const dataUrl = canvas.toDataURL("image/png");
  const base64 = dataUrl.split(",")[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

// ===== BOTTOM overlay: 3 signature boxes =====
const buildSignaturePng = () => {
  const W = 1100;
  const H = 180;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);

  const PURPLE = "#655dc6";

  // Header above the boxes (centered)
  ctx.textBaseline = "middle";
  ctx.fillStyle = PURPLE;
  ctx.font = "600 18px Arial, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("Signatures", 6, 14);
  ctx.textAlign = "right";
  ctx.fillText("التوقيعات", W - 6, 14);

  // Three signature boxes
  const boxTop = 30;
  const boxBottom = H - 4;
  const gap = 16;
  const boxW = (W - gap * 2) / 3;

  const drawBox = (x, labelEn, labelAr) => {
    ctx.strokeStyle = PURPLE;
    ctx.lineWidth = 2.5;
    const r = 12;
    ctx.beginPath();
    ctx.moveTo(x + r, boxTop);
    ctx.lineTo(x + boxW - r, boxTop);
    ctx.quadraticCurveTo(x + boxW, boxTop, x + boxW, boxTop + r);
    ctx.lineTo(x + boxW, boxBottom - r);
    ctx.quadraticCurveTo(x + boxW, boxBottom, x + boxW - r, boxBottom);
    ctx.lineTo(x + r, boxBottom);
    ctx.quadraticCurveTo(x, boxBottom, x, boxBottom - r);
    ctx.lineTo(x, boxTop + r);
    ctx.quadraticCurveTo(x, boxTop, x + r, boxTop);
    ctx.closePath();
    ctx.stroke();

    // Label inside box at the bottom (bilingual, clear)
    ctx.fillStyle = PURPLE;
    ctx.font = "600 16px Arial, sans-serif";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText(labelEn, x + 10, boxBottom - 14);
    ctx.textAlign = "right";
    ctx.fillText(labelAr, x + boxW - 10, boxBottom - 14);
  };

  drawBox(0, "Patient", "المريض");
  drawBox(boxW + gap, "Doctor", "الطبيب");
  drawBox((boxW + gap) * 2, "Witness", "الشاهد");

  const dataUrl = canvas.toDataURL("image/png");
  const base64 = dataUrl.split(",")[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

// (overlay builders defined above)

const Consents = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.role === "admin";
  const [patientName, setPatientName] = useState("");
  const [fileNumber, setFileNumber] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [teeth, setTeeth] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [templates, setTemplates] = useState(CONSENTS);
  const [newTplAr, setNewTplAr] = useState("");
  const [newTplEn, setNewTplEn] = useState("");
  const [newTplFile, setNewTplFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [uploading, setUploading] = useState(false);

  const loadTemplates = () => axios.get(`${API}/consent-templates`)
    .then(r => { if (r.data?.length) setTemplates(r.data); }).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  const uploadTemplate = async () => {
    if (!newTplAr.trim()) { toast.error("العنوان بالعربية مطلوب"); return; }
    if (!newTplFile) { toast.error("اختر ملف PDF للنموذج"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title_ar", newTplAr);
      fd.append("title_en", newTplEn);
      fd.append("file", newTplFile);
      await axios.post(`${API}/consent-templates`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("تمت إضافة النموذج — أصبح متاحاً فوراً");
      setNewTplAr(""); setNewTplEn(""); setNewTplFile(null); setFileInputKey(k => k + 1);
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل رفع النموذج"); }
    finally { setUploading(false); }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`حذف نموذج "${tpl.titleAr}" نهائياً؟`)) return;
    try {
      await axios.delete(`${API}/consent-templates/${tpl.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("تم حذف النموذج");
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل في الحذف"); }
  };

  const generatePDF = async (consent) => {
    if (!patientName.trim()) {
      toast.error("يرجى إدخال اسم المريض");
      return;
    }
    setGenerating(consent.id);
    try {
      // 1. Fetch original PDF
      const res = await fetch(consent.static_file ? `/consents/${consent.static_file}` : `${API}/consent-templates/${consent.id}/file`);
      if (!res.ok) throw new Error("PDF not found");
      const origBytes = await res.arrayBuffer();

      // 2. Load with pdf-lib
      const pdfDoc = await PDFDocument.load(origBytes);
      const pages = pdfDoc.getPages();
      const page = pages[pages.length - 1]; // last page = where the empty space is
      const { width, height } = page.getSize();

      // 3. Build TOP overlay (patient info strip) — placed just below the title
      const topBytes = buildTopOverlayPng({ patientName, fileNumber, nationalId });
      const topPng = await pdfDoc.embedPng(topBytes);

      // Build BOTTOM overlay (signatures)
      const sigBytes = buildSignaturePng();
      const sigPng = await pdfDoc.embedPng(sigBytes);

      // 4. Position the overlays on the page (A4: 595x842pt).
      // Right edge moved LEFT 0.5 inch (36pt): rightInset 68 → 104
      const leftInset = 50;
      const rightInset = 104;
      const stripWidth = width - leftInset - rightInset;

      // TOP STRIP — Patient info (compact: H=96), raised higher
      const stripHeight = (stripWidth * 96) / 1100;
      const stripY = 722;     // raised from 705
      page.drawImage(topPng, {
        x: leftInset,
        y: stripY,
        width: stripWidth,
        height: stripHeight,
      });

      // OPTIONAL TOOTH BAR — just below the patient strip (compact: H=38)
      const teethBarBytes = buildTeethBarPng(teeth);
      if (teethBarBytes) {
        const teethPng = await pdfDoc.embedPng(teethBarBytes);
        const teethBarHeight = (stripWidth * 38) / 1100;
        const teethBarY = stripY - teethBarHeight - 5;
        page.drawImage(teethPng, {
          x: leftInset,
          y: teethBarY,
          width: stripWidth,
          height: teethBarHeight,
        });
      }

      // BOTTOM STRIP — signatures (same horizontal bounds)
      const sigHeight = (stripWidth * 180) / 1100;
      page.drawImage(sigPng, {
        x: leftInset,
        y: 90,
        width: stripWidth,
        height: sigHeight,
      });

      // 5. Save generation record to backend FIRST (before download)
      try {
        await axios.post(`${API}/consent-records`, {
          consent_type: consent.id,
          consent_title: consent.titleAr,
          patient_name: patientName,
          file_number: fileNumber,
          national_id: nationalId,
          teeth: teeth,
        });
      } catch (e) {
        console.error("Failed to record consent:", e);
      }

      // 6. Save and download
      const outBytes = await pdfDoc.save();
      const blob = new Blob([outBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const safeName = (patientName || "patient").replace(/\s+/g, "_");
      const a = document.createElement("a");
      a.href = url;
      const tplSlug = (consent.titleEn || consent.id).replace(/[^\w\u0600-\u06FF-]+/g, "_").slice(0, 40);
      a.download = `Consent_${tplSlug}_${safeName}_${fileNumber || "NA"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("تم توليد الإقرار");
    } catch (e) {
      console.error(e);
      toast.error("فشل توليد الإقرار");
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/lucent-logo.png" alt="LUCENT" className="h-10" />
            <div>
              <h1 className="text-lg font-bold text-[#655dc6]">الإقرارات</h1>
              <p className="text-xs text-slate-500">Lucent Dental Center — Consents</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Patient Info */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>اسم المريض</Label>
                <Input
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  placeholder="أدخل اسم المريض"
                  data-testid="consent-patient-name"
                />
              </div>
              <div className="space-y-2">
                <Label>رقم الملف الطبي</Label>
                <Input
                  value={fileNumber}
                  onChange={(e) => setFileNumber(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="رقم الملف"
                  inputMode="numeric"
                  dir="ltr"
                  data-testid="consent-file-number"
                />
              </div>
              <div className="space-y-2">
                <Label>رقم الهوية</Label>
                <Input
                  value={nationalId}
                  onChange={(e) => setNationalId(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                  placeholder="رقم الهوية (10 أرقام)"
                  inputMode="numeric"
                  dir="ltr"
                  data-testid="consent-national-id"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tooth Chart */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-[#655dc6]" />
              <h3 className="font-semibold text-[#655dc6]">رقم/أرقام الأسنان</h3>
              {teeth.length > 0 && (
                <span className="text-xs text-slate-500">({teeth.length} سن)</span>
              )}
            </div>
            <ToothChart
              selectedTeeth={teeth}
              onToggleTooth={(t) => {
                setTeeth(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
              }}
            />
          </CardContent>
        </Card>

        {/* Consents list */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-[#655dc6]" />
              <h3 className="font-semibold text-[#655dc6]">الإقرارات المتاحة</h3>
            </div>
            <p className="text-xs text-slate-500">
              اختر الإقرار المطلوب لتوليد ملف PDF. سيتم وضع اسم المريض ورقم السن وخانات التواقيع (المريض، الطبيب، الشاهد) في الفراغ السفلي من كل إقرار.
            </p>
            <div className="space-y-2 pt-1">
              {templates.map((c, idx) => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 p-3 bg-[#FAF9FE] rounded-lg border border-slate-100"
                  data-testid={`consent-row-${c.id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-[#655dc6] text-white flex items-center justify-center font-bold text-sm shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{c.titleAr}</p>
                    <p className="text-xs text-slate-500 truncate">{c.titleEn}</p>
                  </div>
                  <Button
                    onClick={() => generatePDF(c)}
                    disabled={generating === c.id}
                    size="sm"
                    className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-1.5 shrink-0"
                    data-testid={`generate-consent-${c.id}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                    {generating === c.id ? "جاري..." : "توليد"}
                  </Button>
                  {isAdmin && (
                    <Button
                      onClick={() => deleteTemplate(c)}
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:bg-red-50 h-8 w-8 p-0 shrink-0"
                      data-testid={`delete-consent-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {isAdmin && (
              <div className="mt-4 border-t pt-4 space-y-2" data-testid="consent-template-manager">
                <div className="flex items-center gap-2">
                  <Upload className="w-4 h-4 text-[#655dc6]" />
                  <h4 className="font-semibold text-sm text-[#655dc6]">إضافة نموذج إقرار جديد (PDF)</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input value={newTplAr} onChange={e => setNewTplAr(e.target.value)} placeholder="عنوان الإقرار بالعربية *" data-testid="new-tpl-ar" />
                  <Input value={newTplEn} onChange={e => setNewTplEn(e.target.value)} placeholder="العنوان بالإنجليزية (اختياري)" dir="ltr" data-testid="new-tpl-en" />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Input key={fileInputKey} type="file" accept="application/pdf" onChange={e => setNewTplFile(e.target.files?.[0] || null)} className="flex-1 min-w-[200px]" data-testid="new-tpl-file" />
                  <Button onClick={uploadTemplate} disabled={uploading} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-1.5" data-testid="upload-tpl-btn">
                    <Upload className="w-3.5 h-3.5" /> {uploading ? "جاري الرفع..." : "رفع النموذج"}
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400">يجب أن يحتوي النموذج على فراغ سفلي في آخر صفحة لوضع بيانات المريض والتواقيع تلقائياً</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Consents;
